import { AuthenticationManager } from '@frontend/authentication-v2';
import { ColliClient } from '@frontend/colli/api';
import { useReactiveCollis } from '@frontend/colli/events';
import { Colli } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { Feature, FeatureTarget, Functionality, FunctionalityType } from '@frontend/functionality/types';
import { LabelClient } from '@frontend/label/api';
import { Label } from '@frontend/label/types';
import { PinCodeClient } from '@frontend/pin-code/api';
import { PinCode } from '@frontend/pin-code/types';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { TransactionActorClient, TransactionClient, TransactionUserClient } from '@frontend/transaction/api';
import { useReactiveTransaction } from '@frontend/transaction/events';
import { Transaction, TransactionActor, TransactionActorType, TransactionStateName, TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate, useParams } from 'react-router-dom';

import useCurrentLocation from '../../../hooks/useCurrentLocation';
import { useMenuRepository } from '../../../menu/menu-repository';
import { isColliAtOtherLocation } from '../../../utils/colli-location';

interface ViewProps {
    navigate: NavigateFunction;
    isLoading: boolean;
    commentsVisible: boolean;
    setCommentsVisible: (visible: boolean) => void;
    transaction: Transaction | null;
    collis: Colli[];
    receivers: TransactionUser[];
    pickedUpBy: TransactionActor | null;
    pinCodes: PinCode[];
    isReceiver: boolean;
    isSender: boolean;
    deleteModal: boolean;
    setDeleteModal: (value: boolean) => void;
    onDelete: (value: boolean) => void;
    onContinue?: () => void;

    canPrint: boolean;
    onPrint: () => void;
    printing: boolean;
    moveInfo: { url: string; functionality: Functionality } | null;
    deviceSpotId: string | undefined;
    isColliAtOtherLocation: (colli: Colli) => boolean;
}

const useTransactionDetail = (): ViewProps => {
    const navigate = useNavigate();
    const location = useLocation();
    const { transactionId } = useParams();
    const { spot_id: deviceSpotId } = useCurrentLocation();
    const menuRepo = useMenuRepository();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [commentsVisible, setCommentsVisible] = useState<boolean>(false);
    const { transaction, isLoading: transactionLoading } = useReactiveTransaction({ transactionId });
    const [colliIds, setColliIds] = useState<string[]>([]);
    const { collis: reactiveCollis, isLoading: collisLoading } = useReactiveCollis({ colliIds, fetchOnload: true });
    const [receivers, setReceivers] = useState<TransactionUser[]>([]);
    const collis = useMemo(() => {
        const byId = new Map(reactiveCollis.map((colli) => [colli.id, colli]));
        return colliIds.map((colliId) => byId.get(colliId)).filter((colli): colli is Colli => Boolean(colli));
    }, [reactiveCollis, colliIds]);

    const [pickedUpBy, setPickedUpBy] = useState<TransactionActor | null>(null);
    const [sender, setSender] = useState<TransactionUser | null>(null);
    const [pinCodes, setPinCodes] = useState<PinCode[]>([]);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);

    useEffect(() => {
        AuthenticationManager.getInstance()
            .getTokenWhenReady()
            .then((token) => setCurrentUserId(token?.entity_id ?? null));
    }, []);
    const [continueInfo, setContinueInfo] = useState<{ wizardId: string; functionalityId: string } | null>(null);
    const mobilePreferredPrinter = localStorage.getItem('mobile-preferred-printer') ? JSON.parse(localStorage.getItem('mobile-preferred-printer')!) : null;
    const [labels, setLabels] = useState<Label[] | null>(null);
    const [printing, setPrinting] = useState<boolean>(false);
    const [moveInfo, setMoveInfo] = useState<{ url: string; functionality: Functionality } | null>(null);

    useEffect(() => {
        if (transaction) {
            setIsLoading(true);
            getTransactionInfo(transaction).finally(() => setIsLoading(false));
        }
    }, [transaction]);

    const getTransactionInfo = async (trx: Transaction) => {
        if (trx) {
            setColliIds([]);
            const colliResult = await ColliClient.fetchCollis({ transaction_id: trx.id });
            setColliIds((colliResult.results || []).map((colli) => colli.id));
            const [transactionUserResult, transactionActorResult] = await Promise.all([
                TransactionUserClient.fetchTransactionUsers({ transaction_id: trx.id }),
                TransactionActorClient.fetchTransactionActors({ transaction_id: trx.id })
            ]);
            setReceivers(transactionUserResult.results.filter((u) => u.type === TransactionUserType.RECEIVER) || []);
            setPickedUpBy(transactionActorResult.results.find((u) => u.type === TransactionActorType.PICKED_UP_BY) || null);
            setSender(transactionUserResult.results.find((u) => u.type === TransactionUserType.SENDER) || null);
            const pinCodeResult = await PinCodeClient.fetchPinCodes({ entity_id: trx.id, entity_type: EntityType.TRANSACTION });
            setPinCodes(pinCodeResult.results || []);

            if (trx.state === TransactionStateName.TRANSACTION_CREATED && trx.application && trx.feature) {
                const functionality = menuRepo
                    .getFunctionalitiesByApplication(trx.application, FunctionalityType.CORPORATE)
                    .find((f) => f.feature === trx.feature);
                if (functionality?.wizard_id) {
                    setContinueInfo({
                        wizardId: functionality.wizard_id,
                        functionalityId: functionality.id
                    });
                } else if (functionality) {
                    SettingClient.fetchSettings({ entity_id: functionality.id, entity_type: EntityType.FUNCTIONALITY }).then((settings) => {
                        const setting = settings.results.find((s) => s.key === SettingKey.WIZARD_ID);
                        if (setting && typeof setting.value === 'string') {
                            setContinueInfo({
                                wizardId: setting.value,
                                functionalityId: functionality.id
                            });
                        }
                    });
                }
            }
        }
    };

    useEffect(() => {
        if (transaction) {
            LabelClient.fetchLabels({ transaction_id: transaction.id }).then((labelResult) => {
                setLabels(labelResult.results);
            });
        }
    }, [transaction]);

    const onPrint = async () => {
        if (mobilePreferredPrinter && transaction && labels && labels.length > 0) {
            setPrinting(true);
            try {
                const printPromises = labels.map((label) => LabelClient.printLabel(transaction.account_id, label.id, { iot_id: mobilePreferredPrinter }));
                await Promise.all(printPromises);
            } finally {
                setPrinting(false);
            }
        }
    };

    useEffect(() => {
        const moveButton = menuRepo.functionalities.find((f) => f.feature === Feature.MOVE_TRANSACTION && f.target === FeatureTarget.API);
        if (moveButton && moveButton.wizard_id) {
            setMoveInfo({
                functionality: moveButton,
                url: `/wizard/${moveButton.id}/${moveButton.wizard_id}?from=${encodeURIComponent(location.pathname + location.search)}`
            });
        }
    }, [menuRepo]);

    const onDelete = () => {
        if (transaction) {
            TransactionClient.deleteTransaction(transaction.account_id, transaction.id);
            navigate(-1);
        }
        setDeleteModal(false);
    };

    const onContinue = continueInfo
        ? () => {
              navigate(`/wizard/${continueInfo.functionalityId}/${continueInfo.wizardId}?from=${encodeURIComponent(location.pathname + location.search)}`, {
                  state: { transaction }
              });
          }
        : undefined;

    return {
        navigate,
        isLoading: isLoading || transactionLoading || (colliIds.length > 0 && collisLoading),
        commentsVisible,
        setCommentsVisible,
        transaction,
        collis,
        receivers,
        pickedUpBy,
        pinCodes,
        isReceiver: !!currentUserId && receivers.some((r) => r.user_id === currentUserId),
        isSender: !!currentUserId && !!sender && sender.user_id === currentUserId,
        deleteModal,
        setDeleteModal,
        onDelete,
        onContinue,

        canPrint: !!(
            mobilePreferredPrinter &&
            labels &&
            labels.length > 0 &&
            transaction?.state &&
            [
                TransactionStateName.TRANSACTION_CREATED,
                TransactionStateName.TRANSACTION_DROP_OFF_PENDING,
                TransactionStateName.TRANSACTION_PICK_UP_PENDING
            ].includes(transaction?.state)
        ),
        onPrint,
        printing,
        moveInfo,
        deviceSpotId,
        isColliAtOtherLocation: (colli) => isColliAtOtherLocation(colli, deviceSpotId)
    };
};

export default useTransactionDetail;
